import { Invoice } from "@/api/opb/opbTypes";
import { useTranslation } from "react-i18next";
import { ReactNode } from "react";
import { LoadingCircle } from "@industriousoffice/react-components";
import InvoiceTableRow from "../../pages/Dashboard/components/InvoiceTableRow";

interface InvoiceTableProps {
  isLoading: boolean;
  invoices: Invoice[];
  currencyCode: string;
  description: string;
  footer?: ReactNode;
  withAction?: boolean;
  onAction?: (invoiceId: string) => void;
  actionText?: string;
  emptyStateText?: string;
  timezone?: string;
}

function InvoiceTable({
  invoices,
  isLoading,
  currencyCode,
  description,
  footer,
  withAction,
  actionText,
  onAction,
  emptyStateText,
  timezone,
}: InvoiceTableProps) {
  const { t } = useTranslation(["common", "dashboard"]);

  return (
    <div className="border rounded-lg border-grey-60 border-separate border-spacing-0">
      <table
        className="table-auto min-w-full w-full"
        data-testid="invoice_table-table"
      >
        <thead>
          <tr>
            <th
              data-testid="invoice_table-issued_date-th"
              className="text-left bg-almond-99 bg-opacity-50 py-2 rounded-t-lg rounded-r-none font-hanken text-sm font-normal text-grey-30 border-b p-4"
            >
              {t("Issued Date")}
            </th>
            <th
              data-testid="invoice_table-number-th"
              className="text-left bg-almond-99 bg-opacity-50 py-2 text-sm font-normal font-hanken text-grey-30 border-b p-4"
            >
              {t("Invoice Number")}
            </th>
            <th
              data-testid="invoice_table-description-th"
              className="text-left bg-almond-99 bg-opacity-50 py-2 text-sm font-normal font-hanken text-grey-30 border-b p-4"
            >
              {t("Description")}
            </th>
            <th
              data-testid="invoice_table-amount-th"
              className="text-left bg-almond-99 bg-opacity-50 py-2 text-sm font-normal font-hanken text-grey-30 border-b p-4"
            >
              {t("Invoice Payment Amount")}
            </th>
            <th
              data-testid="invoice_table-due_date-th"
              className="text-left bg-almond-99 bg-opacity-50 py-2 text-sm font-normal font-hanken text-grey-30 border-b p-4"
            >
              {t("Due Date")}
            </th>
            <th
              data-testid="invoice_table-status-th"
              className="text-left bg-almond-99 bg-opacity-50 py-2 rounded-t-lg rounded-l-none font-hanken text-sm font-normal text-grey-30 border-b p-4"
            >
              {t("Status")}
            </th>
            {withAction ? (
              <th
                data-testid="invoice_table-action-th"
                className="text-left bg-almond-99 bg-opacity-50 py-2 rounded-t-lg rounded-l-none font-hanken text-sm font-normal text-grey-30 border-b p-4"
              >
                {" "}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={6} className="text-center w-screen">
                <div className="flex items-center justify-center py-5">
                  <LoadingCircle spinnerColor="#013e3f" spinnerSize={40} />
                </div>
              </td>
            </tr>
          ) : null}
          {invoices.length === 0 && !isLoading ? (
            <tr>
              <td
                colSpan={6}
                className="font-normal text-left text-base text-grey-30 pl-4 pt-2 pb-2 w-screen font-hanken"
                data-testid="invoice_table-empty_state-td"
              >
                {emptyStateText ?? t("invoiceEmptyState")}
              </td>
            </tr>
          ) : null}
          {invoices.length && !isLoading ? (
            <>
              {invoices.map((invoice) => (
                <InvoiceTableRow
                  key={`${invoice.id}-row`}
                  invoice={invoice}
                  currencyCode={currencyCode}
                  description={description}
                  withAction={withAction}
                  onAction={onAction}
                  actionText={actionText}
                  timezone={timezone}
                />
              ))}
            </>
          ) : null}
        </tbody>
      </table>
      {!isLoading ? footer : null}
    </div>
  );
}

export default InvoiceTable;
